import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {FormInput} from '@vidazoo/ui-kit';
import clsx from 'clsx';
import theme from './theme.module.scss';
import {
    applyFormulaSuggestion,
    formulaSuggestions,
    formulaTokenAt,
    IFormulaMetric,
} from '../../common/utils/formulaAutocomplete';

export interface IFormulaInputProps {
    value: string;
    metrics: IFormulaMetric[];
    onChange: (value: string) => void;
}

const LIST_ID = 'dynamic-formula-suggestions';

/** Formula field that suggests the report's metrics for the operand being typed. */
export default function FormulaInput({value, metrics, onChange}: IFormulaInputProps): React.JSX.Element {
    const container = useRef<HTMLDivElement>(null);
    const [caret, setCaret] = useState<number | null>(null);
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(0);
    const pendingCaret = useRef<number | null>(null);

    const input = () => container.current?.querySelector('input');
    const token = useMemo(() => caret === null ? null : formulaTokenAt(value, caret), [value, caret]);
    const suggestions = useMemo(() => open && token ? formulaSuggestions(token, metrics) : [], [open, token, metrics]);
    const visible = suggestions.length > 0;

    useEffect(() => setActive(0), [token?.query]);

    // Start with the placeholder operand selected, so typing replaces it.
    useEffect(() => {
        const element = input();
        const placeholder = value.indexOf('Metric');
        if (element && placeholder > -1) {
            element.focus();
            element.setSelectionRange(placeholder, placeholder + 'Metric'.length);
        }
    }, []);

    useLayoutEffect(() => {
        if (pendingCaret.current === null) return;
        input()?.setSelectionRange(pendingCaret.current, pendingCaret.current);
        pendingCaret.current = null;
    }, [value]);

    const trackCaret = (element: HTMLInputElement) =>
        setCaret(element.selectionStart === element.selectionEnd ? element.selectionStart : null);

    const choose = (metric: IFormulaMetric) => {
        const next = applyFormulaSuggestion(value, token, metric);
        pendingCaret.current = next.caret;
        setCaret(next.caret);
        setOpen(false);
        onChange(next.value);
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (!visible) return;
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            const step = event.key === 'ArrowDown' ? 1 : -1;
            setActive((active + step + suggestions.length) % suggestions.length);
        } else if (event.key === 'Enter' || event.key === 'Tab') {
            event.preventDefault();
            choose(suggestions[active]);
        } else if (event.key === 'Escape') {
            // Close the list without closing the dialog.
            event.preventDefault();
            event.stopPropagation();
            setOpen(false);
        }
    };

    return (
        <div ref={container} className={theme.formulaField}>
            <FormInput label="Formula" name="formula" aria-label="Formula" value={value}
                placeholder="(a/b)*100" hideValidationMessage hideWhiteSpacesMessage autoComplete="off"
                role="combobox" aria-autocomplete="list" aria-expanded={visible} aria-controls={LIST_ID}
                aria-activedescendant={visible ? `${LIST_ID}-${active}` : undefined}
                onChange={event => {
                    onChange(event.target.value);
                    trackCaret(event.target);
                    setOpen(true);
                }}
                onSelect={event => trackCaret(event.target as HTMLInputElement)}
                onKeyDown={onKeyDown}
                onBlur={() => setOpen(false)}/>
            {visible && (
                <ul id={LIST_ID} role="listbox" aria-label="Metrics" className={theme.suggestions}>
                    {suggestions.map((metric, index) => (
                        <li key={metric.value} id={`${LIST_ID}-${index}`} role="option" aria-selected={index === active}
                            className={clsx(theme.suggestion, index === active && theme.activeSuggestion)}
                            // Keep focus in the input while choosing with the mouse.
                            onMouseDown={event => {
                                event.preventDefault();
                                choose(metric);
                            }}
                            onMouseEnter={() => setActive(index)}>
                            <span className={theme.suggestionLabel}>{metric.label}</span>
                            {metric.value !== metric.label && <span className={theme.suggestionKey}>{metric.value}</span>}
                        </li>
                    ))}
                </ul>
            )}
            <div className={theme.formulaHint}>Type a metric name; ↑ ↓ to choose, Enter or Tab to insert.</div>
        </div>
    );
}
